import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, ArrowDownToLine, Check, Clipboard, ExternalLink, Eye, FileText, Image, Library, LoaderCircle, Mail, Pencil, Plus, Search, Share2, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { http, requestErrorMessage } from "@/lib/erp";
import { useAuth } from "@/context/AuthContext";
import { canShareCatalogue, catalogueAttachment, catalogueFileKind, isValidEmailAddress, validateCatalogueUpload } from "@/lib/catalogue";
import { saveBlob } from "@/lib/legalLetters";
import StatusAvatar from "@/components/StatusAvatar";
import { useWhatsAppSender } from "@/context/WhatsAppSendContext";

const inputClass = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100";
const primaryClass = "inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50";
const secondaryClass = "inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50";
const dangerClass = "inline-flex items-center justify-center gap-2 rounded-md border border-rose-200 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50";
const freshForm = () => ({ title: "", description: "", file: null });
const displayDate = (value) => value ? new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
const displaySize = (bytes) => Number(bytes || 0) < 1024 * 1024
  ? `${Math.max(1, Math.round(Number(bytes || 0) / 1024))} KB`
  : `${(Number(bytes) / (1024 * 1024)).toFixed(1)} MB`;

function Modal({ title, onClose, children, wide = false }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return <div className="fixed inset-0 z-[70] grid place-items-center overflow-y-auto bg-slate-950/50 p-3 sm:p-6" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section role="dialog" aria-modal="true" aria-label={title} className={`my-auto w-full ${wide ? "max-w-5xl" : "max-w-xl"} overflow-hidden rounded-xl bg-white shadow-2xl`}>
      <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
        <div><p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-700">VGRUPS · CATALOGUE</p><h2 className="mt-1 text-lg font-semibold text-slate-900">{title}</h2></div>
        <button type="button" aria-label="Close dialog" onClick={onClose} className="rounded-md p-2 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
      </header>
      {children}
    </section>
  </div>;
}

function ErrorBanner({ children, onDismiss }) {
  if (!children) return null;
  return <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span className="flex-1">{children}</span>
    {onDismiss && <button type="button" aria-label="Dismiss error" onClick={onDismiss} className="text-rose-700"><X size={16} /></button>}
  </div>;
}

export default function Catalogue() {
  const requestWhatsAppSend = useWhatsAppSender();
  const { can, user } = useAuth();
  const canManage = can("manage_masters");
  const isGlobalViewer = String(user?.role || "").trim().toLowerCase() === "viewer";
  const [companyRole, setCompanyRole] = useState("");
  const [roleLoading, setRoleLoading] = useState(true);
  const [roleError, setRoleError] = useState("");
  const canShare = !roleLoading && !roleError && canShareCatalogue(user?.role, companyRole);
  const shareBlockedReason = isGlobalViewer
    ? "Sharing and email are unavailable to users with a global Viewer role."
    : roleLoading
      ? "Checking your company role…"
      : roleError
        ? "Sharing and email are disabled because your company role could not be verified."
        : !companyRole
          ? "Sharing and email are disabled until your company role is verified."
          : "Sharing and email are unavailable to users with a Viewer role.";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [query, setQuery] = useState("");
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(freshForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [workingId, setWorkingId] = useState("");
  const [shareItem, setShareItem] = useState(null);
  const [shareResult, setShareResult] = useState(null);
  const [shareError, setShareError] = useState("");
  const [sharing, setSharing] = useState(false);
  const [emailItem, setEmailItem] = useState(null);
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailStatusLoading, setEmailStatusLoading] = useState(false);
  const [emailStatusError, setEmailStatusError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const loadCompanyRole = useCallback(async () => {
    setRoleLoading(true);
    setRoleError("");
    setCompanyRole("");
    try {
      const { data } = await http.get("/auth/permissions");
      if (typeof data?.role !== "string" || !data.role.trim()) {
        throw new Error("The company role response did not include a role.");
      }
      setCompanyRole(data.role);
    } catch (error) {
      setRoleError(error instanceof Error && !error.response ? error.message : requestErrorMessage(error, "verify your company role"));
    } finally {
      setRoleLoading(false);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await http.get("/catalogue");
      const rows = Array.isArray(data) ? data : data?.items;
      if (!Array.isArray(rows)) throw new Error("The catalogue response was not a list.");
      setItems(rows);
    } catch (error) {
      setLoadError(error instanceof Error && !error.response ? error.message : requestErrorMessage(error, "load the catalogue"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadCompanyRole(); }, [loadCompanyRole]);

  useEffect(() => {
    if (!preview) { setPreviewUrl(""); setPreviewError(""); setPreviewLoading(false); return undefined; }
    let cancelled = false;
    let ownedUrl = "";
    setPreviewUrl("");
    setPreviewError("");
    setPreviewLoading(true);
    http.get(`/catalogue/${encodeURIComponent(preview.id)}/file`, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        ownedUrl = URL.createObjectURL(data);
        setPreviewUrl(ownedUrl);
      })
      .catch((error) => { if (!cancelled) setPreviewError(requestErrorMessage(error, "open this catalogue file")); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => {
      cancelled = true;
      if (ownedUrl) URL.revokeObjectURL(ownedUrl);
    };
  }, [preview]);

  const filteredItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => [item.title, item.description, item.filename].some((value) => String(value || "").toLowerCase().includes(needle)));
  }, [items, query]);

  const openCreate = () => {
    setForm(freshForm());
    setFormError("");
    setEditor({ mode: "create" });
  };
  const openEdit = async (item) => {
    setFormError("");
    try {
      const { data } = await http.get(`/catalogue/${encodeURIComponent(item.id)}`);
      setForm({ title: data.title || "", description: data.description || "", file: null });
      setEditor({ mode: "edit", id: item.id });
    } catch (error) {
      setActionError(requestErrorMessage(error, "open this catalogue item for editing"));
    }
  };
  const chooseFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const error = validateCatalogueUpload(file);
    if (error) {
      setFormError(error);
      event.target.value = "";
      return;
    }
    setForm((current) => ({ ...current, file }));
    setFormError("");
  };
  const saveItem = async (event) => {
    event.preventDefault();
    setFormError("");
    if (!form.title.trim()) { setFormError("A title is required."); return; }
    if (editor.mode === "create" && !form.file) { setFormError("Choose a PDF, JPEG or PNG file to upload."); return; }
    setSaving(true);
    try {
      if (editor.mode === "create") {
        const attachment = await catalogueAttachment(form.file);
        await http.post("/catalogue", { title: form.title.trim(), description: form.description.trim(), attachment });
      } else {
        await http.put(`/catalogue/${encodeURIComponent(editor.id)}`, { title: form.title.trim(), description: form.description.trim() });
      }
      setEditor(null);
      setForm(freshForm());
      setActionError("");
      await loadItems();
    } catch (error) {
      setFormError(error instanceof Error && !error.response ? error.message : requestErrorMessage(error, editor.mode === "create" ? "upload this file" : "save these changes"));
    } finally {
      setSaving(false);
    }
  };
  const deleteItem = async (item) => {
    if (!window.confirm(`Delete “${item.title}” and its file? This cannot be undone.`)) return;
    setWorkingId(String(item.id));
    setActionError("");
    try {
      await http.delete(`/catalogue/${encodeURIComponent(item.id)}`);
      await loadItems();
    } catch (error) {
      setActionError(requestErrorMessage(error, "delete this catalogue item"));
    } finally { setWorkingId(""); }
  };
  const downloadItem = async (item) => {
    setDownloading(String(item.id));
    setActionError("");
    try {
      const { data, headers } = await http.get(`/catalogue/${encodeURIComponent(item.id)}/file?download=true`, { responseType: "blob" });
      const headerName = headers?.["content-disposition"]?.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)?.[1];
      const filename = item.filename || (headerName ? decodeURIComponent(headerName.replace(/"/g, "")) : "catalogue-file");
      saveBlob(data, filename);
    } catch (error) {
      const message = requestErrorMessage(error, "download this catalogue file");
      if (String(preview?.id) === String(item.id)) setPreviewError(message);
      else setActionError(message);
    } finally { setDownloading(""); }
  };
  const openShare = (item) => {
    setShareItem(item);
    setShareResult(null);
    setShareError("");
  };
  const createShare = async () => {
    if (!shareItem) return;
    setSharing(true);
    setShareError("");
    try {
      const { data } = await http.post(`/catalogue/${encodeURIComponent(shareItem.id)}/share`);
      if (!data?.public_path) throw new Error("The server did not return a public link.");
      setShareResult(data);
    } catch (error) {
      setShareError(error instanceof Error && !error.response ? error.message : requestErrorMessage(error, "create a public link"));
    } finally { setSharing(false); }
  };
  const shareUrl = shareResult?.public_path ? `${window.location.origin}${shareResult.public_path}` : "";
  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareError("");
    } catch {
      setShareError("Could not copy the link. Copy it directly from the field above.");
    }
  };
  const revokeShare = async (item) => {
    if (!window.confirm(`Revoke every public link for “${item.title}”? Existing links will stop working.`)) return;
    setWorkingId(String(item.id));
    setActionError("");
    try {
      await http.delete(`/catalogue/${encodeURIComponent(item.id)}/share`);
      setActionError("");
    } catch (error) {
      setActionError(requestErrorMessage(error, "revoke public links"));
    } finally { setWorkingId(""); }
  };
  const openEmail = async (item) => {
    setEmailItem(item);
    setEmail("");
    setEmailError("");
    setEmailStatus(null);
    setEmailStatusError("");
    setEmailStatusLoading(true);
    try {
      const { data } = await http.get("/catalogue/email-status");
      if (typeof data?.available !== "boolean") throw new Error("Email service returned an unexpected status.");
      setEmailStatus(data);
    } catch (error) {
      setEmailStatusError(error instanceof Error && !error.response ? error.message : requestErrorMessage(error, "check email availability"));
    } finally { setEmailStatusLoading(false); }
  };
  const sendEmail = async (event) => {
    event.preventDefault();
    setEmailError("");
    if (!isValidEmailAddress(email)) { setEmailError("Enter a valid recipient email address."); return; }
    if (!emailStatus?.available) return;
    setSendingEmail(true);
    try {
      await http.post(`/catalogue/${encodeURIComponent(emailItem.id)}/email`, { email: email.trim() });
      toast.success(`Catalogue file emailed to ${email.trim()}.`);
      setEmailItem(null);
    } catch (error) {
      setEmailError(requestErrorMessage(error, "send this email"));
    } finally { setSendingEmail(false); }
  };

  return <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
    <section className="overflow-hidden rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50 via-white to-slate-50 shadow-sm">
      <div className="flex flex-col gap-6 p-6 sm:p-8 md:flex-row md:items-center md:justify-between">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-700"><Library size={14} /> Shared company files</div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Company catalogue</h1>
          <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">Browse approved PDFs and images, search by title or description, and share a time-limited public link when needed.</p>
        </div>
        {canManage && <button type="button" onClick={openCreate} className={primaryClass}><Plus size={17} /> Upload a file</button>}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-brand-100 bg-white/60 px-6 py-3 text-xs text-slate-500 sm:px-8">
        <span>PDF · JPEG · PNG</span><span>Maximum file size 5 MB</span><span>Public links expire after 30 days</span>
      </div>
    </section>

    {(loadError || actionError) && <ErrorBanner onDismiss={() => { setLoadError(""); setActionError(""); }}>{loadError || actionError}</ErrorBanner>}
    {roleLoading && <p role="status" className="text-sm text-slate-500">Verifying your company role before enabling share and email actions…</p>}
    {roleError && <div className="space-y-2"><ErrorBanner>{roleError}</ErrorBanner><button type="button" className={secondaryClass} disabled={roleLoading} onClick={loadCompanyRole}>Retry role check</button></div>}
    {!roleLoading && !roleError && !canShare && <p role="status" className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">{shareBlockedReason}</p>}

    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div><h2 className="text-lg font-semibold text-slate-900">Files <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{items.length}</span></h2><p className="mt-1 text-sm text-slate-500">Search the company’s catalogue.</p></div>
        <label className="relative block w-full sm:max-w-sm">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={`${inputClass} pl-9`} aria-label="Search catalogue" placeholder="Search title, description or filename…" value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
      </div>
      {loading ? <StatusAvatar label="Loading catalogue…" className="min-h-44 p-6" />
        : loadError ? <div className="p-6 text-center"><button className={secondaryClass} type="button" onClick={loadItems}>Retry loading</button></div>
          : filteredItems.length === 0 ? <div className="px-5 py-16 text-center">
            <StatusAvatar type="cloud" state={items.length ? "default" : "sleeping"} size={72} />
            <h3 className="mt-4 font-semibold text-slate-800">{items.length ? "No matching files" : "No files in the catalogue yet"}</h3>
            <p className="mt-1 text-sm text-slate-500">{items.length ? "Try a different search." : canManage ? "Upload a PDF, JPEG or PNG to get started." : "Company files added by your team will appear here."}</p>
          </div> : <div className="grid gap-3 p-4 sm:p-5 md:grid-cols-2 xl:grid-cols-3">
            {filteredItems.map((item) => {
              const kind = catalogueFileKind(item.content_type);
              return <article key={item.id} className="flex min-w-0 flex-col rounded-xl border border-slate-200 bg-white p-4 transition hover:border-brand-200 hover:shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-700">{kind === "image" ? <Image size={20} /> : <FileText size={20} />}</div>
                  <div className="min-w-0 flex-1"><h3 className="break-words font-semibold text-slate-900">{item.title}</h3><p className="mt-1 truncate text-xs text-slate-500" title={item.filename}>{item.filename}</p></div>
                  <span className="shrink-0 rounded bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase text-slate-600">{kind === "unknown" ? item.content_type?.split("/")[1] || "FILE" : kind}</span>
                </div>
                {item.description && <p className="mt-3 line-clamp-3 whitespace-pre-line text-sm leading-5 text-slate-600">{item.description}</p>}
                <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500"><span>{displaySize(item.size_bytes)}</span><span>Added {displayDate(item.created_at)}</span></div>
                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  <button type="button" className={secondaryClass} onClick={() => { setPreview(item); setPreviewError(""); }}><Eye size={15} /> View</button>
                  <button type="button" className={secondaryClass} disabled={downloading === String(item.id)} onClick={() => downloadItem(item)}><ArrowDownToLine size={15} /> {downloading === String(item.id) ? "Downloading…" : "Download"}</button>
                  <button type="button" className={secondaryClass} disabled={!canShare} title={!canShare ? shareBlockedReason : undefined} onClick={() => openShare(item)}><Share2 size={15} /> Share</button><button type="button" className={secondaryClass} disabled={!canShare} title={!canShare ? shareBlockedReason : undefined} onClick={() => openEmail(item)}><Mail size={15} /> Email</button>
                  {canManage && <><button type="button" className={secondaryClass} onClick={() => openEdit(item)}><Pencil size={15} /> Edit</button><button type="button" className={secondaryClass} disabled={workingId === String(item.id)} onClick={() => revokeShare(item)} title="Revoke every public link for this file"><X size={15} /> Revoke links</button><button type="button" className={dangerClass} disabled={workingId === String(item.id)} onClick={() => deleteItem(item)}><Trash2 size={15} /> Delete</button></>}
                </div>
              </article>;
            })}
          </div>}
    </section>

    {editor && <Modal title={editor.mode === "create" ? "Upload catalogue file" : "Edit catalogue details"} onClose={() => { if (!saving) setEditor(null); }}>
      <form onSubmit={saveItem} className="space-y-4 p-5 sm:p-6">
        <label className="block space-y-1.5 text-sm font-medium text-slate-700">Title <span className="text-rose-600">*</span>
          <input className={inputClass} required maxLength={160} value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="A clear file title" />
        </label>
        <label className="block space-y-1.5 text-sm font-medium text-slate-700">Description <span className="font-normal text-slate-400">Optional</span>
          <textarea className={`${inputClass} resize-y`} rows={3} maxLength={2000} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="Add context to help your team find this file" />
        </label>
        {editor.mode === "create" && <div className="space-y-2">
          <span className="block text-sm font-medium text-slate-700">File <span className="text-rose-600">*</span></span>
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-4 hover:border-brand-400 hover:bg-brand-50/40">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-white text-brand-700 shadow-sm"><Upload size={17} /></span>
            <span className="min-w-0"><span className="block truncate text-sm font-medium text-slate-800">{form.file?.name || "Choose a PDF, JPEG or PNG"}</span><span className="block text-xs text-slate-500">Maximum 5 MB</span></span>
            <input type="file" className="sr-only" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={chooseFile} />
          </label>
        </div>}
        {formError && <ErrorBanner>{formError}</ErrorBanner>}
        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
          <button type="button" className={secondaryClass} disabled={saving} onClick={() => setEditor(null)}>Cancel</button>
          <button type="submit" className={primaryClass} disabled={saving}>{saving ? <><LoaderCircle size={16} className="animate-spin" /> Saving…</> : editor.mode === "create" ? <><Upload size={16} /> Upload file</> : <><Check size={16} /> Save changes</>}</button>
        </div>
      </form>
    </Modal>}

    {preview && <Modal title={preview.title} wide onClose={() => setPreview(null)}>
      <div className="space-y-3 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600"><span className="break-all">{preview.filename}</span><button type="button" className={secondaryClass} disabled={downloading === String(preview.id)} onClick={() => downloadItem(preview)}><ArrowDownToLine size={15} /> Download</button></div>
        {previewError && <ErrorBanner>{previewError}</ErrorBanner>}
        {previewLoading && <StatusAvatar type="drop" label="Loading file…" className="h-[55vh]" />}
        {!previewLoading && previewUrl && catalogueFileKind(preview.content_type) === "pdf" && <iframe className="h-[62vh] min-h-[350px] w-full rounded-lg border border-slate-200" src={previewUrl} title={`PDF preview: ${preview.title}`} />}
        {!previewLoading && previewUrl && catalogueFileKind(preview.content_type) === "image" && <div className="grid max-h-[68vh] min-h-48 place-items-center overflow-auto rounded-lg bg-slate-100 p-3"><img className="max-h-[64vh] max-w-full object-contain" src={previewUrl} alt={preview.title} /></div>}
        {!previewLoading && previewUrl && catalogueFileKind(preview.content_type) === "unknown" && <ErrorBanner>This file type cannot be previewed in the browser. Download it to open the file.</ErrorBanner>}
      </div>
    </Modal>}

    {shareItem && <Modal title={`Share “${shareItem.title}”`} onClose={() => { if (!sharing) setShareItem(null); }}>
      <div className="space-y-4 p-5 sm:p-6">
        {!shareResult ? <>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950"><p className="font-semibold">Anyone with the link can view this file.</p><p className="mt-1">The link is public and expires after 30 days. Create it only if you are comfortable sharing this file outside your company.</p></div>
          {shareError && <ErrorBanner>{shareError}</ErrorBanner>}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><button type="button" className={secondaryClass} disabled={sharing} onClick={() => setShareItem(null)}>Cancel</button><button type="button" className={primaryClass} disabled={sharing} onClick={createShare}>{sharing ? <><LoaderCircle size={16} className="animate-spin" /> Creating link…</> : <><Share2 size={16} /> Create public link</>}</button></div>
        </> : <>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"><p className="font-semibold">Public link created</p><p className="mt-1">Anyone with this link can view the file{shareResult.expires_at ? ` until ${displayDate(shareResult.expires_at)}` : " for up to 30 days"}.</p></div>
          <label className="block space-y-1.5 text-sm font-medium text-slate-700">Share link
            <input className={inputClass} readOnly value={shareUrl} onFocus={(event) => event.target.select()} />
          </label>
          {shareError && <ErrorBanner>{shareError}</ErrorBanner>}
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" className={`${secondaryClass} flex-1`} onClick={copyShare}><Clipboard size={16} /> Copy link</button>
            <button type="button" className={`${secondaryClass} flex-1`} onClick={() => { requestWhatsAppSend({ message: `${shareItem.title}\n${shareUrl}`, title: `Catalogue — ${shareItem.title}` }); setShareItem(null); }}><ExternalLink size={16} /> Send via WhatsApp</button>
          </div>
           <p className="text-xs leading-5 text-slate-500">Enter the recipient’s phone number, review the message and confirm to send through your connected WhatsApp account.</p>
          <div className="flex justify-end border-t border-slate-100 pt-3"><button type="button" className={secondaryClass} onClick={() => setShareItem(null)}>Done</button></div>
        </>}
      </div>
    </Modal>}

    {emailItem && <Modal title={`Email “${emailItem.title}”`} onClose={() => { if (!sendingEmail) setEmailItem(null); }}>
      <form onSubmit={sendEmail} className="space-y-4 p-5 sm:p-6">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm"><p className="font-medium text-slate-800">File will be sent as an email attachment</p><p className="mt-1 break-words text-slate-600">{emailItem.title} · {emailItem.filename}</p><p className="mt-1 text-xs text-slate-500">This sends the file directly through the connected email service. No public link is created.</p></div>
        {emailStatusLoading && <p className="inline-flex items-center gap-2 text-sm text-slate-500"><LoaderCircle size={15} className="animate-spin" /> Checking email service…</p>}
        {emailStatusError && <ErrorBanner>{emailStatusError}</ErrorBanner>}
        {!emailStatusLoading && emailStatus && !emailStatus.available && <ErrorBanner>{emailStatus.reason || "Email delivery is currently unavailable."}</ErrorBanner>}
        {emailStatus?.available && <label className="block space-y-1.5 text-sm font-medium text-slate-700">Recipient email
          <input className={inputClass} type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com" />
        </label>}
        {emailError && <ErrorBanner>{emailError}</ErrorBanner>}
        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
          <button type="button" className={secondaryClass} disabled={sendingEmail} onClick={() => setEmailItem(null)}>Cancel</button>
          <button type="submit" className={primaryClass} disabled={sendingEmail || emailStatusLoading || !emailStatus?.available || !!emailStatusError}>{sendingEmail ? <><LoaderCircle size={16} className="animate-spin" /> Sending…</> : <><Mail size={16} /> Send email</>}</button>
        </div>
      </form>
    </Modal>}
  </div>;
}